<template>
  <div class="color-facet">
    <MultiSelectDefault
      :facet="facet"
      :is-dynamic="isDynamic"
      :custom-value-renderer="customValueRenderer"
      :render-during-ssr="renderDuringSsr"
      :show-checkbox="false"
    />
  </div>
</template>
<script setup>
import { h } from 'vue';
import useFacetUrl from '@components/FilteringFacets/composables/useFacetUrl';
import MultiSelectDefault from './MultiSelectDefault';

const props = defineProps({
  facet: {
    type: Object,
    required: true,
  },
  isDynamic: {
    type: Boolean,
    default: () => false,
  },
  renderDuringSsr: {
    type: Boolean,
    default: false,
  },
});

const { generateFacetUrl } = useFacetUrl();

function customValueRenderer(value) {
  const renderedValue = {
    label: value.label,
    ariaLabel: value.label,
    value: value.value,
    labelType: 'html',
    url: props.renderDuringSsr ? generateFacetUrl(value.url) : value.url,
  };
  renderedValue.label = h('div', { class: 'color-item-container', innerHTML: `<div class="item_icon" label="${value.label}" title="${value.value} (${value.count})"></div><div class="item_label" title="${value.value} (${value.count})">${value.label}</div>` });
  return renderedValue;
}
</script>
<style scoped lang="scss" brand="mcom">
:deep(.checkbox-container){
 .color-item-container {
    .item_icon {
      width: 36px;
      height: 36px;
      border-radius: 50%;
    }
    }
    input[name=criteria-checkbox]{
      &:checked+label {
        border-radius: 50%;
      }
      &:checked+label div.item_icon::after {
          border-radius: 50%;
          left: -3.5px;
          top: -3.5px;
      }
    }
    a[aria-checked=true]{
      .color-item-container {
        border-radius: 50%;
      }
      .color-item-container div.item_icon::after {
        border-radius: 50%;
        left: -3.5px;
        top: -3.5px;
      }
    }
  }
</style>
<style scoped lang="scss" brand="bcom">
:deep(.checkbox-container){
 .color-item-container {
    .item_icon {
      width: 37px;
      height: 37px;
    }
  }
  .checkbox-wrapper{
    input[name=criteria-checkbox]{
      &:checked+label div.item_icon::after {
          left: -3px;
          top: -3px;
      }
    }
    a[aria-checked=true]{
      .color-item-container div.item_icon::after {
          left: -3px;
          top: -3px;
      }
    }
    &:last-child{
      @include breakpoint(medium down) {
        margin-bottom: 24px !important;
      }
    }
  }
}
</style>
<style scoped lang="scss">
:deep(.checkbox-container){
  margin: 2px 0 0 4px;
  .checkbox-wrapper{
    &:has(a){
    padding-top: rem-calc(2px);
    }
    display: inline-grid;
    label {
      position: relative;
    }
    input[name=criteria-checkbox]{
      opacity:0;
      position:absolute;

      &:focus+label div.item_icon {
        outline: 1px solid black;
      }

      +label div.item_icon {
        border: 1px solid #eee;
        padding-top: 0px;
      }
      &:checked+label div.item_icon::after {
          content: "";
          border: 1px solid black;
          position: absolute;
          width: 43px;
          height: 43px;
      }
    }

    a{
      &:focus{
        outline: none;
      }
      &:focus div.item_icon {
        outline: 1px solid black;
      }

      div.item_icon {
        border: 1px solid #eee;
        padding-top: 0px;
        cursor: pointer;
      }
    }

    a[aria-checked=true]{
      .color-item-container{
        div.item_icon::after {
          content: "";
          border: 1px solid black;
          position: absolute;
          width: 43px;
          height: 43px;
        }
      }
    }

    &:last-child{
      margin-bottom: 16px;
    }
  }
  display: grid;
  grid-template-columns: repeat(6, 0fr);
  gap: 16px 16px;
  @include breakpoint(large) {
    grid-column-gap: 4.7%;
  }
  .color-item-container{
    position: relative;
    max-width: 41px;
    .item_icon{
      cursor:pointer;
    }
    .item_label{
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      margin-top: 12px;
      text-align:center;
      min-width: 41px;
    }
    .item_icon[title^="Red"]{
      background: var(--filtering-facets-color-gradient-red);
    }
    .item_icon[title^="Silver"]{
      background: var(--filtering-facets-color-gradient-silver);
    }
    .item_icon[title^="Multi"]{
      background-image: var(--filtering-facets-color-gradient-multi);
    }
    .item_icon[title^="Yellow"]{
      background: var(--filtering-facets-color-gradient-yellow);
    }
    .item_icon[title^="Black"]{
      background: var(--filtering-facets-color-gradient-black);
    }
    .item_icon[title^="Blue"]{
      background: var(--filtering-facets-color-gradient-blue);
    }
    .item_icon[title^="Brown"]{
      background: var(--filtering-facets-color-gradient-brown);
    }
    .item_icon[title^="Gold"]{
      background: var(--filtering-facets-color-gradient-gold);
    }
    .item_icon[title^="Ivory"]{
      background: var(--filtering-facets-color-gradient-ivory);
    }
    .item_icon[title^="Gray"]{
      background: var(--filtering-facets-color-gradient-gray);
    }
    .item_icon[title^="Green"]{
      background: var(--filtering-facets-color-gradient-green);
    }
    .item_icon[title^="Orange"]{
      background: var(--filtering-facets-color-gradient-orange);
    }
    .item_icon[title^="Pink"]{
      background: var(--filtering-facets-color-gradient-pink);
    }
    .item_icon[title^="Purple"]{
      background: var(--filtering-facets-color-gradient-purple);
    }
    .item_icon[title^="Tan"]{
      background: var(--filtering-facets-color-gradient-tan);
    }
    .item_icon[title^="White"]{
      background: var(--filtering-facets-color-gradient-white);
    }
  }
}
</style>
